
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface ResourceUsageStats {
  userBotsCount: number;
  premiumBotAccountsCount: number;
  propBotAccountsCount: number;
  tradingAccountsCount: number;
  cspAccountsCount: number;
}

export function useResourceUsage(userId: string | undefined) {
  return useQuery({
    queryKey: ['resourceUsage', userId],
    queryFn: async (): Promise<ResourceUsageStats> => {
      if (!userId) return {
        userBotsCount: 0,
        premiumBotAccountsCount: 0,
        propBotAccountsCount: 0,
        tradingAccountsCount: 0,
        cspAccountsCount: 0
      };

      // Get user bots count (user_bot type)
      const { data: userBotsData, error: userBotsError } = await supabase
        .from('bots')
        .select('id')
        .eq('owner_id', userId)
        .eq('type', 'user_bot');
      
      if (userBotsError) console.error("Error fetching user bots:", userBotsError);
      
      // Get premium bot accounts count
      const { data: premiumBotAccountsData } = await supabase
        .from('bot_accounts')
        .select('bot_id')
        .eq('user_id', userId)
        .in('bot_id', (await supabase
          .from('bots')
          .select('id')
          .eq('type', 'premium_bot')).data?.map(bot => bot.id) || []);
      
      // Get prop bot accounts count
      const { data: propBotAccountsData } = await supabase
        .from('bot_accounts')
        .select('bot_id')
        .eq('user_id', userId)
        .in('bot_id', (await supabase
          .from('bots')
          .select('id')
          .eq('type', 'prop_bot')).data?.map(bot => bot.id) || []);
      
      // Get trading accounts count (JOIN with csp_accounts to filter by user_id)
      const { data: tradingAccountsData } = await supabase
        .from('api_connections')
        .select('id, csp_accounts!inner(user_id)')
        .eq('csp_accounts.user_id', userId);
      
      // Get cTrader profiles count (csp_accounts)
      const { data: cspAccountsData } = await supabase
        .from('csp_accounts')
        .select('id')
        .eq('user_id', userId);
      
      return {
        userBotsCount: userBotsData?.length || 0,
        premiumBotAccountsCount: premiumBotAccountsData?.length || 0,
        propBotAccountsCount: propBotAccountsData?.length || 0,
        tradingAccountsCount: tradingAccountsData?.length || 0,
        cspAccountsCount: cspAccountsData?.length || 0,
      };
    },
    enabled: !!userId,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });
}
